import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PublicSiteHeader from "../components/PublicSiteHeader";

const dataSourcesSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://www.reignitespirit.com/data-sources#webpage",
  url: "https://www.reignitespirit.com/data-sources",
  name: "Data Sources",
  description:
    "Information about the occupational, labour-market and user-provided data considered by Reignite Spirit.",
  isPartOf: {
    "@id": "https://www.reignitespirit.com/#website",
  },
};

export default function DataSourcesPage() {
  return (
    <div className="min-h-screen bg-[#0F0A24] text-white">
      <SEO
        title="Career Intelligence Data Sources"
        description="Review the categories of occupational, labour-market, user-provided and financial information used to support Reignite Spirit career intelligence."
        path="/data-sources"
        image="/og-data-sources.jpg"
        type="website"
        author="Reignite Spirit"
        keywords="career data sources, labour market data, occupational data, O*NET career data, career intelligence sources"
        schema={dataSourcesSchema}
      />

      <PublicSiteHeader />

      <main className="px-6 py-16">
        <article className="max-w-5xl mx-auto">
          <header className="text-center mb-14">
            <p className="text-[#EB6AB5] font-bold uppercase tracking-[0.25em] text-sm">
              Data Sources
            </p>

            <h1 className="text-4xl md:text-6xl font-extrabold mt-4">
              The information behind our career intelligence
            </h1>

            <p className="mt-6 max-w-3xl mx-auto text-lg text-white/70 leading-relaxed">
              Reignite Spirit may use a combination of user-provided information,
              structured occupational frameworks, public labour-market sources
              and internal analytical rules.
            </p>
          </header>

          <div className="grid md:grid-cols-2 gap-6">
            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">User-provided information</h2>
              <p className="text-white/70 leading-relaxed">
                Information entered by users may include occupation, location,
                experience, skills, career goals, income, savings, expenses,
                financial obligations and other transition-related details.
              </p>
              <p className="text-white/60 leading-relaxed mt-4">
                The accuracy of outputs depends partly on the accuracy and
                completeness of the information supplied.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">Occupational frameworks</h2>
              <p className="text-white/70 leading-relaxed">
                Structured occupational frameworks may be used to understand job
                activities, skills, knowledge areas, work contexts and
                relationships between occupations.
              </p>
              <p className="text-white/60 leading-relaxed mt-4">
                Where appropriate, this may include publicly available
                occupational information such as O*NET-aligned role and skill
                classifications.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">Labour-market information</h2>
              <p className="text-white/70 leading-relaxed">
                Labour-market context may include job availability, hiring
                signals, employer demand, location relevance, salary indicators
                and changing skill requirements.
              </p>
              <p className="text-white/60 leading-relaxed mt-4">
                Availability and quality can vary substantially by city, country,
                occupation and time period.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">Technology and automation indicators</h2>
              <p className="text-white/70 leading-relaxed">
                Career-risk analysis may consider how work tasks could be
                affected by automation, generative AI, software adoption and
                changes in organizational processes.
              </p>
              <p className="text-white/60 leading-relaxed mt-4">
                These indicators represent informed estimates and should not be
                interpreted as definitive predictions that a role will disappear.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">Financial assumptions</h2>
              <p className="text-white/70 leading-relaxed">
                Financial tools may use user inputs together with simplified
                assumptions about monthly expenses, savings, inflation,
                employment benefits, gratuity, severance or other transition
                resources.
              </p>
              <p className="text-white/60 leading-relaxed mt-4">
                These assumptions may not reflect current law, tax treatment,
                employer policy or personal eligibility.
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">Internal analytical rules</h2>
              <p className="text-white/70 leading-relaxed">
                Reignite Spirit may apply internal scoring rules, matching logic,
                weighted criteria and report-generation instructions to organize
                information and produce user-facing insights.
              </p>
              <p className="text-white/60 leading-relaxed mt-4">
                These methods may be reviewed and updated as the platform develops.
              </p>
            </section>
          </div>

          <section className="mt-10 rounded-3xl border border-amber-400/30 bg-amber-400/5 p-7">
            <h2 className="text-2xl font-bold text-amber-100 mb-4">
              Data freshness and coverage
            </h2>
            <p className="text-amber-100/80 leading-relaxed">
              No source provides complete, real-time coverage of every
              occupation, employer or location. Some data may be historical,
              sampled, estimated or unavailable. Users should verify important
              decisions using current job listings, official government sources,
              employers and qualified advisers.
            </p>
          </section>

          <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-7">
            <div className="flex flex-wrap gap-3">
              <Link
                to="/methodology"
                className="px-5 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold"
              >
                Review our methodology
              </Link>

              <Link
                to="/disclaimer"
                className="px-5 py-3 rounded-full border border-white/15 hover:border-[#EB6AB5]"
              >
                Read limitations
              </Link>
            </div>
          </section>
        </article>
      </main>
    </div>
  );
}
